import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/utils/AuthContext';
import { projectsApi, marketAnalysesApi } from '@/utils/api-pb';
import type { Project, MarketAnalysis, Persona } from '@/utils/pocketbase';
import { pb } from '@/utils/pocketbase';
import { API_URL } from '@/utils/config';
import { GenerationProgress } from '@/components/shared/GenerationProgress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';
interface PersonaCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (persona: Persona) => void;
  persona?: Persona | null;
  isEditing?: boolean;
}

export function PersonaCreationModal({
  isOpen,
  onClose,
  onSuccess,
  persona = null,
  isEditing = false,
}: PersonaCreationModalProps) {
  const { t } = useTranslation('personas');
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { subscriptionTier } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [marketAnalyses, setMarketAnalyses] = useState<MarketAnalysis[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [selectedMarketAnalysisId, setSelectedMarketAnalysisId] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [comment, setComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const targetAudienceRef = useRef<HTMLTextAreaElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      loadProjects();

      if (isEditing && persona) {
        setSelectedProjectId(persona.project);
        const data = persona.persona_data as Record<string, unknown> | undefined;
        setTargetAudience((data?.target_audience as string) || '');
        setSelectedMarketAnalysisId((data?.market_analysis_id as string) || '');
        setGender((data?.gender as string) || '');
        setAge((data?.age as string) || '');
        setComment((data?.comment as string) || '');
      } else {
        resetForm();
      }
    }
  }, [isOpen, isEditing, persona]);

  useEffect(() => {
    if (selectedProjectId) {
      loadMarketAnalyses(selectedProjectId);
    } else {
      setMarketAnalyses([]);
      setSelectedMarketAnalysisId('');
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (targetAudienceRef.current) {
      targetAudienceRef.current.style.height = 'auto';
      targetAudienceRef.current.style.height = `${targetAudienceRef.current.scrollHeight}px`;
    }
  }, [targetAudience]);

  useEffect(() => {
    if (commentRef.current) {
      commentRef.current.style.height = 'auto';
      commentRef.current.style.height = `${commentRef.current.scrollHeight}px`;
    }
  }, [comment]);

  const loadProjects = async () => {
    try {
      const list = await projectsApi.list();
      setProjects(list);
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  const loadMarketAnalyses = async (projectId: string) => {
    try {
      const list = await marketAnalysesApi.list(projectId);
      setMarketAnalyses(list);
    } catch (err) {
      console.error('Error loading market analyses:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedProjectId) {
      setError(t('modal.error.selectProduct'));
      return;
    }

    setIsLoading(true);

    try {
      const url = isEditing && persona
        ? `${API_URL}/api/ai/personas/${persona.id}`
        : `${API_URL}/api/ai/personas`;

      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${pb.authStore.token}`,
        },
        body: JSON.stringify({
          projectId: selectedProjectId,
          target_audience: targetAudience || undefined,
          market_analysis_id: selectedMarketAnalysisId || undefined,
          gender: gender || undefined,
          age: age || undefined,
          comment: comment || undefined,
          lang,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          setError(data.message || t('modal.error.limitReached'));
          navigate('/subscription');
        } else {
          setError(data.error || t('modal.error.createFailed'));
        }
        return;
      }

      onSuccess(data.persona);
      resetForm();
    } catch (err) {
      console.error('Error creating persona:', err);
      setError(t('modal.error.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedProjectId('');
    setTargetAudience('');
    setSelectedMarketAnalysisId('');
    setGender('');
    setAge('');
    setComment('');
    setError('');
  };

  if (!isOpen) return null;

  // Note: isFree can be used for future restrictions
  void subscriptionTier; // Mark as intentionally unused for now

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-[16px]"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) {
          resetForm();
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-[24px] p-[24px] md:p-[40px] max-w-[580px] w-full max-h-[90vh] overflow-y-auto">
        {isLoading ? (
          <GenerationProgress
            title={t('modal.progressTitle')}
            message={isEditing
              ? t('modal.progressMessageEdit')
              : t('modal.progressMessageCreate')}
          />
        ) : (
        <>
        <h2 className="font-manrope font-bold text-[32px] leading-[40px] text-[color:var(--black-text,#0d0d0d)] mb-[24px]">
          {isEditing ? t('modal.titleEdit') : t('modal.titleCreate')}
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-[16px]">
          {/* Project Selection */}
          <div className="flex flex-col gap-[8px] w-full">
            <label className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
              {t('modal.labelProduct')}
            </label>
            <Select
              value={selectedProjectId}
              onValueChange={(value) => setSelectedProjectId(value)}
              disabled={isEditing}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('modal.placeholderProduct')} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Target Audience Description */}
          <div className="flex flex-col gap-[8px] w-full">
            <label className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
              {t('modal.labelTargetAudience')}
            </label>
            <textarea
              ref={targetAudienceRef}
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder={t('modal.placeholderTargetAudience')}
              className="w-full px-[16px] py-[12px] rounded-[16px] bg-transparent border border-[#eeeeee] font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] placeholder:text-[#969696] resize-none overflow-hidden outline-none min-h-[96px]"
            />
          </div>

          {/* Market Analysis Selection */}
          <div className="flex flex-col gap-[8px] w-full">
            <label className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
              {t('modal.labelMarketAnalysis')}
            </label>
            <Select
              value={selectedMarketAnalysisId}
              onValueChange={(value) => setSelectedMarketAnalysisId(value)}
              disabled={!selectedProjectId}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('modal.placeholderProduct')} />
              </SelectTrigger>
              <SelectContent>
                {marketAnalyses.map((analysis) => {
                  const analysesForProject = marketAnalyses.filter(
                    (a) => a.project === analysis.project
                  );
                  const shouldShowNumber = analysesForProject.length > 1;
                  const displayNumber =
                    analysesForProject.findIndex((a) => a.id === analysis.id) + 1;
                  const project = projects.find((p) => p.id === analysis.project);

                  return (
                    <SelectItem key={analysis.id} value={analysis.id}>
                      {t('modal.analysisItemTitle', { projectName: project?.name || t('modal.analysisItemTitleDefault') })}
                      {shouldShowNumber ? ` ${displayNumber}` : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Gender Selection */}
          <div className="flex flex-col gap-[8px] w-full">
            <label className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
              {t('modal.labelGender')}
            </label>
            <Select
              value={gender}
              onValueChange={(value) => setGender(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('modal.placeholderGender')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={lang === 'en' ? 'Male' : 'Мужской'}>{t('modal.genderMale')}</SelectItem>
                <SelectItem value={lang === 'en' ? 'Female' : 'Женский'}>{t('modal.genderFemale')}</SelectItem>
                <SelectItem value={lang === 'en' ? 'Any' : 'Любой'}>{t('modal.genderAny')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Age Input */}
          <div className="flex flex-col gap-[8px] w-full">
            <label className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
              {t('modal.labelAge')}
            </label>
            <input
              type="text"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder={t('modal.placeholderAge')}
              className="w-full px-[16px] py-[12px] rounded-[16px] bg-transparent border border-[#eeeeee] font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] placeholder:text-[#969696] outline-none"
            />
          </div>

          {/* Comment */}
          <div className="flex flex-col gap-[8px] w-full">
            <label className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
              {t('modal.labelComment')}
            </label>
            <textarea
              ref={commentRef}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('modal.placeholderComment')}
              className="w-full px-[16px] py-[12px] rounded-[16px] bg-transparent border border-[#eeeeee] font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] placeholder:text-[#969696] resize-none overflow-hidden outline-none min-h-[96px]"
            />
          </div>

          {error && (
            <div className="bg-transparent border border-[#ff9196] rounded-[16px] p-[16px]">
              <p className="font-segoe text-[14px] leading-[20px] text-[#ff5b63] text-center">
                {error}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-[16px] mt-[8px]">
            <button
              type="button"
              onClick={() => {
                resetForm();
                onClose();
              }}
              disabled={isLoading}
              className="bg-transparent border border-[var(--grey-accent,#eeeeee)] border-solid text-[color:var(--black-text,#0d0d0d)] px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] text-center hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1] transition-colors flex-1"
            >
              {t('modal.buttonClose')}
            </button>
            <button
              type="submit"
              disabled={isLoading || !selectedProjectId}
              className={`${
                selectedProjectId && !isLoading
                  ? 'bg-[var(--main-brand,#2197f1)] hover:opacity-90'
                  : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed'
              } text-[color:var(--white,#ffffff)] px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] text-center transition-opacity flex-1`}
            >
              {isLoading
                ? isEditing
                  ? t('modal.buttonUpdating')
                  : t('modal.buttonCreating')
                : isEditing
                  ? t('modal.buttonUpdate')
                  : t('modal.buttonCreate')}
            </button>
          </div>
        </form>
        </>
        )}
      </div>
    </div>
  );
}
